// Report assembly: the single place where a scan's raw observations become the
// analyst-facing finding list.
//
// This module owns *sequence* and nothing else. It decides which rule runs
// when, numbers the findings, dedupes them, sorts them, and evaluates the
// capability gate. It holds no rule text and no rule condition: every finding
// id the product can emit is defined in one of two files, and this one is not
// either of them.
//
//   detection-rules.ts    fixed ids. A `BehaviorRule` matched against a
//                         context's values, or a scan rule whose condition is
//                         computed (integrity, decode counts, IOC extraction,
//                         the entry list, the finding set so far).
//   observed-findings.ts  derived ids. One finding per thing observed --
//                         an action, a flow, a payload, a YARA rule, a
//                         fingerprint, a known hash, a family signature, a
//                         vulnerable dependency. The id is the observation.
//
// `STATIC_RULE_IDS` and `DERIVED_RULE_FAMILIES` in `detection-rules.ts` index
// both. Start there, not here.
//
// **Order is the contract.** Findings are appended, never mutated, and
// `finding-${n}` ids are assigned in insertion order; the report is compared
// byte for byte. The calls below are therefore in scan order, and the order is
// not incidental:
//
//   - the behaviour loop runs first, so correlations can see every rule id;
//   - `behaviorByEntry` is snapshotted *before* the masquerade rule, because
//     that rule must not be able to feed its own correlation;
//   - the archive correlations run after the advisory block, because a pair
//     over the finding set is only meaningful once the set is complete;
//   - the capability gate runs last, because it asks whether any critical
//     finding was raised at all.
//
// Reordering any of these changes the report.

import { checkDependencyAdvisories } from "./advisories/match";
import type { detectActions } from "./action-detector";
import type { AdvisoryManifest } from "./advisories/dataset";
import {
  behaviorByEntry,
  BEHAVIOR_RULES,
  type DetectionContext,
  emitArchiveCorrelations,
  emitArchiveFindings,
  emitEntryCorrelations,
  emitExfiltrationFinding,
  emitIncompleteSignatureFinding,
  emitIntegrityFindings,
  emitMasqueradeFinding,
  emitObfuscationNamingFinding,
  emitReputationFinding,
  emitStringEncryptionFindings,
  emitUnprovenCapabilityNote,
  evidenceFor,
  type RuleSink,
  valuesByEntry,
} from "./detection-rules";
import { assessJarIntegrity } from "./integrity";
import {
  emitActionFindings,
  emitDependencyFindings,
  emitFamilySignatureFindings,
  emitFingerprintFindings,
  emitFlowFindings,
  emitKnownHashFindings,
  emitPayloadFindings,
  emitYaraFindings,
} from "./observed-findings";
import { lookupOfficialModrinthFile } from "./official-reputation";
import { inspectEmbeddedPayloads } from "./payload-inspector";
import { analyzeSymbolicDataFlow } from "./symbolic-engine";
import {
  detectFamilySignatures,
  matchFingerprints,
} from "./threat-intel";
import type {
  ArchiveEntry,
  ClassInfo,
  Dependency,
  DependencyAdvisory,
  EntryPoint,
  Finding,
  Indicator,
  KnownHashMatch,
  Severity,
} from "./types";
import { scanYara } from "./yara";
export interface FindingsInput {
  /** One entry per analysed source, values already decoded and expanded. */
  contexts: DetectionContext[];
  actions: ReturnType<typeof detectActions>;
  classes: ClassInfo[];
  entryPoints: EntryPoint[];
  archiveEntries: ArchiveEntry[];
  unsafePaths: string[];
  dependencies: Dependency[];
  signatureFiles: string[];
  knownHashMatches: KnownHashMatch[];
  /** Per-source counts of xor-packed and character-array values found. */
  decodedValues: {
    xorBySource: Map<string, number>;
    characterPackedBySource: Map<string, number>;
  };
  staticRecoveryBySource: Map<string, Map<string, number>>;
  symbolic: ReturnType<typeof analyzeSymbolicDataFlow>;
  embeddedPayloads: ReturnType<typeof inspectEmbeddedPayloads>;
  integrity: Awaited<ReturnType<typeof assessJarIntegrity>>;
  officialFile: Awaited<ReturnType<typeof lookupOfficialModrinthFile>>;
  yaraMatches: ReturnType<typeof scanYara>;
  fingerprintMatches: ReturnType<typeof matchFingerprints>;
  familySignatureMatches: ReturnType<typeof detectFamilySignatures>;
  analyzedFileName: string;
  entryIsReachable: (entry: string) => boolean;
  referencedByReachableCode: (path: string) => boolean;
  hasReachabilityRoots: boolean;
}

export interface FindingsResult {
  findings: Finding[];
  indicators: Indicator[];
  dependencyAdvisories: DependencyAdvisory[];
  advisoryUnchecked: { source: string; reason: string }[];
  advisoryDataset: AdvisoryManifest | null;
  advisoryDatasetError: string | null;
  advisoryWarnings: string[];
  capabilityProven: boolean;
}
function extractIndicators(contexts: DetectionContext[]) {
  const indicators: Indicator[] = [];
  const seen = new Set<string>();
  let id = 0;
  const add = (
    type: Indicator["type"],
    value: string,
    entry: string,
    risk: Indicator["risk"],
  ) => {
    const normalized = value.replace(/[),.;]+$/, "");
    const key = `${type}:${normalized.toLowerCase()}`;
    if (!normalized || seen.has(key) || indicators.length >= 750) return;
    seen.add(key);
    indicators.push({
      id: `ioc-${++id}`,
      type,
      value: normalized,
      entry,
      risk,
    });
  };

  for (const context of contexts) {
    const body = context.values.join("\n");
    const webhook =
      /https?:\/\/(?:canary\.|ptb\.)?(?:discord(?:app)?\.com)\/api\/webhooks\/\d+\/[A-Za-z0-9._-]+/gi;
    for (const match of body.matchAll(webhook)) {
      add("webhook", match[0], context.source, "high");
    }

    const telegram = /\b\d{8,12}:[A-Za-z0-9_-]{30,50}\b/g;
    for (const match of body.matchAll(telegram)) {
      add("telegram", match[0], context.source, "high");
    }

    const urls = /https?:\/\/[^\s"'<>()[\]{}\\]+/gi;
    for (const match of body.matchAll(urls)) {
      try {
        const host = new URL(match[0]).hostname;
        const routableLookingHost =
          host.includes(".") ||
          /^(?:(?:25[0-5]|2[0-4]\d|1?\d?\d)\.){3}(?:25[0-5]|2[0-4]\d|1?\d?\d)$/.test(
            host,
          );
        if (
          routableLookingHost &&
          !/^(?:localhost|127\.0\.0\.1)$/i.test(host)
        ) {
          add("url", match[0], context.source, "medium");
          add("domain", host, context.source, "context");
        }
      } catch {
        // Partial or malformed fragments stay in the string view, not the IOC list.
      }
    }

    const ipv4 =
      /\b(?:(?:25[0-5]|2[0-4]\d|1?\d?\d)\.){3}(?:25[0-5]|2[0-4]\d|1?\d?\d)\b/g;
    for (const match of body.matchAll(ipv4)) {
      if (!/^(?:127\.|0\.0\.0\.0$|255\.255\.255\.255$)/.test(match[0])) {
        add("ipv4", match[0], context.source, "context");
      }
    }

    const emails = /\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,24}\b/gi;
    for (const match of body.matchAll(emails)) {
      add("email", match[0], context.source, "context");
    }
  }
  return indicators;
}
export async function assembleFindings(
  input: FindingsInput,
): Promise<FindingsResult> {
  const {
    contexts,
    actions,
    classes,
    entryPoints,
    archiveEntries,
    unsafePaths,
    dependencies,
    signatureFiles,
    knownHashMatches,
    decodedValues,
    staticRecoveryBySource,
    symbolic,
    embeddedPayloads,
    integrity,
    officialFile,
    yaraMatches,
    fingerprintMatches,
    familySignatureMatches,
    analyzedFileName,
    entryIsReachable,
    referencedByReachableCode,
    hasReachabilityRoots,
  } = input;

  const findings: Finding[] = [];
  const findingKeys = new Set<string>();
  const advisoryUnchecked: { source: string; reason: string }[] = [];
  const dependencyAdvisories: DependencyAdvisory[] = [];
  let advisoryDataset: AdvisoryManifest | null = null;
  let advisoryDatasetError: string | null = null;
  // Coverage gaps go in one place. YARA already reports the rules that could
  // not run here, and the advisory gaps used to stay in the rail, so a report
  // that left the app lost them. Every surface reads this one list.
  const advisoryWarnings: string[] = [];
  let findingId = 0;
  const add: RuleSink = (
    rule,
    entry: string,
  ) => {
    const key = `${rule.id}:${entry}`;
    if (findingKeys.has(key) || findings.length >= 800) return;
    findingKeys.add(key);
    findings.push({
      id: `finding-${++findingId}`,
      ruleId: rule.id,
      severity: rule.severity,
      confidence: rule.confidence,
      category: rule.category,
      title: rule.title,
      description: rule.description,
      entry,
      evidence: rule.evidence,
      tags: rule.tags,
      recommendation: rule.recommendation,
    });
  };

  // 1. Behaviour rules: the pattern table, once per detection context.
  for (const context of contexts) {
    if (!entryIsReachable(context.source)) continue;
    for (const rule of BEHAVIOR_RULES) {
      const evidence = evidenceFor(rule, context.values);
      if (evidence.length) add({ ...rule, evidence }, context.source);
    }
  }

  // 2. Derived families, in scan order.
  emitActionFindings(actions, { hasReachabilityRoots }, add);
  emitFlowFindings(symbolic.traces, { hasReachabilityRoots }, add);
  emitPayloadFindings(
    embeddedPayloads,
    { referencedByReachableCode },
    add,
  );

  // 3. Scan rules, interleaved with the identity matches they sit beside.
  emitIntegrityFindings(integrity, add);
  emitReputationFinding({ officialFile, analyzedFileName }, add);
  emitStringEncryptionFindings({ decodedValues, staticRecoveryBySource }, add);
  emitYaraFindings(yaraMatches, add);
  emitFingerprintFindings(fingerprintMatches, add);
  emitKnownHashFindings(knownHashMatches, add);
  emitFamilySignatureFindings(familySignatureMatches, add);

  // 4. Same-source correlations. The id snapshot is taken before the
  //    masquerade rule runs, so that rule cannot feed its own siblings.
  const perEntryValues = valuesByEntry(contexts);
  const perEntryIds = behaviorByEntry(findings);
  emitMasqueradeFinding(perEntryValues, add);
  emitEntryCorrelations(
    { behaviorByEntry: perEntryIds, valuesByEntry: perEntryValues },
    add,
  );

  const indicators = extractIndicators(contexts);
  emitExfiltrationFinding({ indicators, entryIsReachable }, add);
  emitArchiveFindings(
    { unsafePaths, archiveEntries, referencedByReachableCode },
    add,
  );

  // Advisory findings come from the vendored OSV dataset rather than a
  // hand-written list, so every id, score and fix version in the finding is
  // something upstream actually published. See src/lib/advisories/README.md.
  if (dependencies.length) {
    let advisoryCheck: Awaited<ReturnType<typeof checkDependencyAdvisories>>;
    try {
      advisoryCheck = await checkDependencyAdvisories(dependencies);
    } catch (error) {
      // A dataset that will not load is a coverage gap, not a reason to throw
      // away the rest of the analysis. Say so loudly instead.
      advisoryDatasetError =
        error instanceof Error ? error.message : String(error);
      advisoryWarnings.push(
        `The vulnerability dataset could not be loaded (${advisoryDatasetError}), so none of the ${dependencies.length.toLocaleString()} resolved dependencies were checked for known advisories. Treat this as no advisory coverage rather than as a clean result.`,
      );
      advisoryCheck = {
        matches: [],
        unresolvable: [],
        dataset: null,
      };
    }
  dependencyAdvisories.push(
    ...emitDependencyFindings(
      { matches: advisoryCheck.matches, dataset: advisoryCheck.dataset },
      add,
    ),
  );
    for (const entry of advisoryCheck.unresolvable) {
      advisoryUnchecked.push(entry);
    }
    advisoryDataset = advisoryCheck.dataset;
    if (advisoryDataset) {
      // Named from the manifest rather than discovered at scan time: these are
      // gaps in the dataset itself, so they apply to every scan that uses it.
      if (advisoryDataset.unresolvableRecords > 0) {
        advisoryWarnings.push(
          `${advisoryDataset.unresolvableRecords.toLocaleString()} advisories in the ${advisoryDataset.source} dataset (cutoff ${advisoryDataset.fetchedAt}) have no affected range that could be matched to a Maven coordinate, so they were never evaluated against this sample. A dependency they affect would not be reported here. For example: ${advisoryDataset.unresolvableSample.slice(0, 3).join("; ")}.`,
        );
      }
      if (advisoryDataset.withoutCvss > 0) {
        advisoryWarnings.push(
          `${advisoryDataset.withoutCvss.toLocaleString()} of ${advisoryDataset.advisoryCount.toLocaleString()} advisories in the dataset (cutoff ${advisoryDataset.fetchedAt}) publish no scoreable CVSS vector. Any that match are still reported, but they are ranked at the lowest severity because no rating could be read, not because they were assessed as low risk.`,
        );
      }
    }
  } else {
    advisoryWarnings.push(
      "No Maven coordinates could be resolved from this sample, so no dependency advisory lookup was performed. That is different from resolving coordinates and finding nothing vulnerable.",
    );
  }

  // 5. Archive-wide correlations, over the complete finding set.
  emitArchiveCorrelations(findings, add);
  emitObfuscationNamingFinding(classes, add);
  emitIncompleteSignatureFinding(signatureFiles, add);

  // JarLab's stated rule is that suspicious code only moves the verdict once it
  // can be tied to an entry point or a proven data-flow path. An archive with
  // neither, and with no hash or family corroboration, is a library surface: it
  // cannot start itself, and every capability rule it trips is unproven. Those
  // findings stay listed in full, they just stop deciding the verdict.
  // A Main-Class or a mod/plugin descriptor means the archive is meant to run.
  // A META-INF/services provider only means some host may call into it, which
  // every ordinary library declares, so it does not prove capability by itself.
  // An unreachable low-severity trace is not an active path either: a build
  // plugin's local file copy was enough to un-gate everything.
  const provenFlow = symbolic.traces.some(
    (trace) =>
      trace.reachable ||
      trace.severity === "critical" ||
      trace.severity === "high" ||
      trace.severity === "medium",
  );
  const capabilityProven =
    entryPoints.some((entryPoint) => entryPoint.type !== "service") ||
    provenFlow ||
    knownHashMatches.length > 0 ||
    familySignatureMatches.length > 0 ||
    findings.some((finding) => finding.severity === "critical");
  emitUnprovenCapabilityNote({ capabilityProven, findings }, add);

  findings.sort((left, right) => {
    const order: Record<Severity, number> = {
      critical: 0,
      high: 1,
      medium: 2,
      low: 3,
      info: 4,
    };
    return order[left.severity] - order[right.severity];
  });

  return {
    findings,
    indicators,
    dependencyAdvisories,
    advisoryUnchecked,
    advisoryDataset,
    advisoryDatasetError,
    advisoryWarnings,
    capabilityProven,
  };
}
